import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { THEMES, themeName, type ThemeId } from "@smartvow/shared";
import { Button, Card, EmptyState, Field, Input } from "../../shared/ui/index.js";
import { ThemeSelect } from "../events/ThemeSelect.js";
import { fmtDate, fmtDateTime, plural } from "../../shared/format.js";
import { useToast } from "../../shared/toast.js";
import { useCreateEvent } from "./weddings.api.js";
import type { EventRow } from "../../shared/types.js";

export function EventsPanel({ weddingId, events }: { weddingId: string; events: EventRow[] }) {
  const navigate = useNavigate();
  const create = useCreateEvent(weddingId);
  const toast = useToast();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [loc, setLoc] = useState("");
  const [theme, setTheme] = useState<ThemeId>(THEMES[0].id);
  const [open, setOpen] = useState("");
  const [close, setClose] = useState("");

  return (
    <Card id="events">
      <h2 className="font-serif text-lg text-stone-900">Événements</h2>

      <ul className="mt-4 flex flex-col gap-2">
        {events.map((ev) => (
          <li key={ev.id}>
            <Link
              to="/app/events/$id"
              params={{ id: ev.id }}
              className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 px-4 py-3 transition hover:border-stone-300"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ background: THEMES.find((t) => t.id === ev.theme)?.swatches[1] }}
                  />
                  <span className="truncate font-medium text-stone-900">{ev.name}</span>
                </span>
                <span className="mt-0.5 block text-xs text-stone-500">
                  {fmtDateTime(ev.date)}
                  {ev.location ? ` · ${ev.location}` : ""} · {themeName(ev.theme)}
                </span>
              </span>
              <span className="shrink-0 text-right text-xs text-stone-400">
                {ev.mediaCount ?? 0} {plural(ev.mediaCount ?? 0, "média")}
                <span className="block">ouverture {fmtDate(ev.revealAt)}</span>
              </span>
            </Link>
          </li>
        ))}
        {events.length === 0 && (
          <EmptyState>Aucun événement — créez la cérémonie, la réception…</EmptyState>
        )}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            {
              name,
              date: new Date(date).toISOString(),
              location: loc,
              theme,
              ...(open ? { captureStartAt: new Date(open).toISOString() } : {}),
              ...(close ? { captureEndAt: new Date(close).toISOString() } : {}),
            },
            {
              onSuccess: (r) => navigate({ to: "/app/events/$id", params: { id: r.id } }),
              onError: () => toast("Vérifiez le nom et la date", "err"),
            },
          );
        }}
        className="mt-6 flex flex-col gap-3 border-t border-stone-100 pt-6"
      >
        <p className="text-sm font-medium text-stone-700">Nouvel événement</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Field label="Nom (Cérémonie, Réception…)">
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Date & heure">
            <Input
              type="datetime-local"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Lieu (facultatif)">
          <Input value={loc} onChange={(e) => setLoc(e.target.value)} />
        </Field>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Field label="Contributions — ouverture (sinon : la date)">
            <Input type="datetime-local" value={open} onChange={(e) => setOpen(e.target.value)} />
          </Field>
          <Field label="Contributions — fermeture (sinon : +6 h)">
            <Input type="datetime-local" value={close} onChange={(e) => setClose(e.target.value)} />
          </Field>
        </div>
        <div>
          <p className="mb-2 text-sm text-stone-600">Thème</p>
          <ThemeSelect value={theme} onChange={setTheme} />
        </div>
        <div>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Création…" : "Ajouter l'événement"}
          </Button>
        </div>
        <p className="text-xs text-stone-400">
          Les invités ne peuvent contribuer qu'entre l'ouverture et la fermeture. La galerie se
          révèle 24 h après la date (modifiable dans les réglages de l'événement).
        </p>
      </form>
    </Card>
  );
}
